import { parse, parseSetCookie, serialize } from 'cookie-es';
import dayjs from 'dayjs';
import { appendResponseHeader } from 'h3';

/**
 * Checks if the session is expired by comparing the current time with the session's expiration time.
 *
 * @param {MaybeRef<object>} session
 * @returns {boolean} - Returns true if the session is expired, false otherwise.
 */
export const isSessionExpired = (session) => {
    const _session = unref(session);

    if (!_session || !_session.expiresAt) {
        // If session is not defined or expiresAt is not set, consider it expired
        return false;
    }

    // Check if expiresAt date on session is in the past
    return _session.expiresAt && dayjs(_session.expiresAt).isBefore(dayjs());
};

const handleAuthCookies = (headers, serverEvent, sessionName) => {
    // Forward the Set-Cookie header to the main server event
    if (import.meta.server && serverEvent) {
        for (const setCookie of headers.getSetCookie()) {
            appendResponseHeader(serverEvent, 'Set-Cookie', setCookie);
            // Update session cookie for next fetch requests
            const { name, value } = parseSetCookie(setCookie);
            if (name === sessionName) {
                const cookies = parse(serverEvent.headers.get('cookie') || '');
                // set or overwrite existing cookie
                cookies[name] = value;
                // update cookie event header for future requests
                serverEvent.headers.set(
                    'cookie',
                    Object.entries(cookies)
                        .map(([name, value]) => serialize(name, value))
                        .join('; '),
                );
                // Also apply to serverEvent.node.req.headers
                if (serverEvent.node?.req?.headers) {
                    serverEvent.node.req.headers['cookie'] =
                        serverEvent.headers.get('cookie') || '';
                }
            }
        }
    }
};

/**
 * Utility functions for handling user authentication in a Nuxt.js application.
 *
 * @param {object} payload
 * @param {object} session - use UserSession composable
 * @returns
 */
export const signIn = async (payload, session) => {
    const { fetch, loggedIn, user } = session;

    const response = await $fetch('/api/auth/login', {
        method: 'POST',
        body: payload,
    });

    if (response.success === false) {
        console.error('Login failed:', response);
        return;
    }

    await fetch();

    if (!loggedIn.value) {
        console.error('User is not logged in after login attempt');
        return;
    }

    if (!user.value?.frontendManagementAccess) {
        navigateTo('/members/dashboard');
        return;
    }

    navigateTo(getAdminDashboardFromProfile(user));
};

/**
 * Logs out the user and redirects to the specified path or the default logout path.
 * @param {string|null} redirect - The path to redirect to after logout. If null, redirects to the default logout path.
 */
export const logout = (redirect = null) => {
    const localePath = useLocalePath();

    redirect = localePath(redirect);

    const localizedLogout = localePath('/auth/signout');

    if (redirect) {
        navigateTo(`${localizedLogout}?redirect=${redirect}`);
        return;
    }

    navigateTo(localizedLogout);
};

/**
 * Refreshes the user's access token by making a request to the server.
 */
export const refreshAccessToken = async () => {
    // Token Cookie
    const refreshTokenCookie = useCookie('px.refreshToken');

    if (!refreshTokenCookie.value) {
        console.warn('No refresh token found, skipping access token refresh');
        throw createError({
            statusCode: 401,
            statusMessage: 'No refresh token found',
        });
    }

    // Server-side handling of access token refresh
    if (import.meta.server) {
        const serverEvent = useRequestEvent();
        const runtimeConfig = useRuntimeConfig();

        return await useRequestFetch()('/api/auth/refresh', {
            onResponse({ response: { headers } }) {
                // Handle cookies from the response headers
                handleAuthCookies(
                    headers,
                    serverEvent,
                    runtimeConfig.session.name,
                );
            },
        });
    }

    // Client-side fetch for user session
    return await $fetch('/api/auth/refresh', {
        credentials: 'include',
    });
};

/**
 * Fetches the user session data from the server.
 *
 * @returns {Promise<any>}
 */
export const fetchUser = async (accessToken = undefined) => {
    if (import.meta.server) {
        const serverEvent = useRequestEvent();
        const runtimeConfig = useRuntimeConfig();

        // Refresh the user session
        return await useRequestFetch()('/api/auth/user', {
            onResponse({ response: { headers } }) {
                handleAuthCookies(
                    headers,
                    serverEvent,
                    runtimeConfig.session.name,
                );
            },
        });
    }

    const { session } = useUserSession();

    accessToken = accessToken ?? session.value.accessToken;

    if (!accessToken) {
        console.warn('No access token found, skipping user fetch');
        return;
    }

    // Client-side fetch for user session
    return await $fetch('/api/auth/user', {
        headers: {
            Authorization: `Bearer ${session.accessToken}`,
        },
    });
};
